import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { getRiskColor, getRiskIcon, calculateRefund, extractDeductionPercentage } from '../../utils/helpers';
import { 
  ExclamationTriangleIcon, 
  CheckCircleIcon, 
  ChartBarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import axios from 'axios';

var userEmail;
var JSToken;
var accessToken;
if (localStorage.user) {
  userEmail = localStorage.user_email;
  JSToken = JSON.parse(localStorage.user);
  accessToken = JSToken.access_token;
}
const config = {
  headers: { Authorization: `Bearer ${accessToken}` },
};


// ==================== BehavioralPatternsCard.jsx ====================
export const BehavioralPatternsCard = ({ delegateEmail }) => {
  const [patterns, setPatterns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPatterns = async () => {
      try {
        setLoading(true);
         const response = await axios.get(
          `https://dash.bestdiplomats.org/api/method/get_behavioral_patterns?delegate_email=${delegateEmail}`,
          config
        );
        if (response.status===200) {
          setPatterns(response.data.data);
        } else {
          setError(response.error);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (delegateEmail) {
      fetchPatterns();
    }
  }, [delegateEmail]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <Skeleton height={30} width={250} className="mb-4" />
        <Skeleton count={4} height={60} className="mb-3" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-red-600">Error loading behavioral patterns: {error}</div>
      </div>
    );
  }

  const RiskIndicator = ({ level }) => (
    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold ${getRiskColor(level)}`}>
      <span className="text-lg">{getRiskIcon(level)}</span>
      <span>{level} Risk</span>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="bg-gradient-to-r from-rose-500 to-pink-600 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-white">Behavioral Patterns & Risk Assessment</h3>
            <p className="text-rose-100 text-sm">Analysis of transfer behavior</p>
          </div>
          <RiskIndicator level={patterns?.risk_level} />
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center">
            <p className="text-xs text-gray-500 font-medium mb-1">Total Transfers</p>
            <p className="text-3xl font-bold text-gray-900">{patterns?.total_transfers}</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 text-center">
            <p className="text-xs text-blue-600 font-medium mb-1">Last 6 Months</p>
            <p className="text-3xl font-bold text-blue-900">{patterns?.transfers_6m}</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200 text-center">
            <p className="text-xs text-purple-600 font-medium mb-1">Last 12 Months</p>
            <p className="text-3xl font-bold text-purple-900">{patterns?.transfers_12m}</p>
          </div>
          <div className={`rounded-lg p-4 border text-center ${
            patterns?.approval_rate >= 75 ? 'bg-green-50 border-green-200' : 
            patterns?.approval_rate >= 50 ? 'bg-yellow-50 border-yellow-200' : 
            'bg-red-50 border-red-200'
          }`}>
            <p className={`text-xs font-medium mb-1 ${
              patterns?.approval_rate >= 75 ? 'text-green-600' : 
              patterns?.approval_rate >= 50 ? 'text-yellow-600' : 
              'text-red-600'
            }`}>Approval Rate</p>
            <p className={`text-3xl font-bold ${
              patterns?.approval_rate >= 75 ? 'text-green-900' : 
              patterns?.approval_rate >= 50 ? 'text-yellow-900' : 
              'text-red-900'
            }`}>{patterns?.approval_rate}%</p>
          </div>
        </div>

        {/* Status Breakdown */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <ChartBarIcon className="h-5 w-5" />
            Request Status Breakdown
          </h4>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-2">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{patterns?.approved_count}</p>
              <p className="text-xs text-gray-500">Approved</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-2">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{patterns?.rejected_count}</p>
              <p className="text-xs text-gray-500">Rejected</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-full mb-2">
                <ClockIcon className="h-6 w-6 text-purple-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{patterns?.appealed_count}</p>
              <p className="text-xs text-gray-500">Appealed</p>
            </div>
          </div>
        </div>

        {/* Behavioral Indicators */}
        <div className="space-y-3">
          <h4 className="font-semibold text-gray-900">Behavioral Indicators</h4>
          
          {/* Last Minute Transfers */}
          <div className={`flex items-start gap-3 p-3 rounded-lg border ${
            patterns?.last_minute_count > 2 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
          }`}>
            <ExclamationTriangleIcon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
              patterns?.last_minute_count > 2 ? 'text-red-600' : 'text-green-600'
            }`} />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">Last-Minute Transfers less than 30 days</p>
              <p className="text-sm text-gray-600 mt-1">
                {patterns?.last_minute_count} transfer{patterns?.last_minute_count !== 1 ? 's' : ''} requested with less than 30 days notice
                {patterns?.last_minute_count > 2 && (
                  <span className="text-red-600 font-semibold"> - Pattern of last-minute requests detected</span>
                )}
              </p>
            </div>
            <span className={`text-2xl font-bold ${
              patterns?.last_minute_count > 2 ? 'text-red-600' : 'text-green-600'
            }`}>
              {patterns?.last_minute_count}
            </span>
          </div>

          {/* Average Days Before Event */}
          <div className="flex items-start gap-3 p-3 rounded-lg border bg-blue-50 border-blue-200">
            <ClockIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">Average Planning Time</p>
              <p className="text-sm text-gray-600 mt-1">
                Typically requests transfers {patterns?.avg_days_before_event} days before the event
              </p>
            </div>
            <span className="text-2xl font-bold text-blue-600">
              {patterns?.avg_days_before_event}
            </span>
          </div>

          {/* Transfer Frequency Alert */}
          {patterns?.transfers_6m > 3 && (
            <div className="flex items-start gap-3 p-3 rounded-lg border bg-orange-50 border-orange-200">
              <ExclamationTriangleIcon className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-orange-900">High Transfer Frequency</p>
                <p className="text-sm text-orange-700 mt-1">
                  User has requested {patterns.transfers_6m} transfers in the last 6 months. This exceeds normal patterns.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Risk Assessment Summary */}
        <div className={`p-4 rounded-lg border-2 ${
          patterns?.risk_level === 'Low' ? 'bg-green-50 border-green-300' :
          patterns?.risk_level === 'Medium' ? 'bg-yellow-50 border-yellow-300' :
          'bg-red-50 border-red-300'
        }`}>
          <h4 className={`font-semibold mb-2 ${
            patterns?.risk_level === 'Low' ? 'text-green-900' :
            patterns?.risk_level === 'Medium' ? 'text-yellow-900' :
            'text-red-900'
          }`}>
            Risk Assessment Summary
          </h4>
          <p className={`text-sm ${
            patterns?.risk_level === 'Low' ? 'text-green-700' :
            patterns?.risk_level === 'Medium' ? 'text-yellow-700' :
            'text-red-700'
          }`}>
            {patterns?.risk_level === 'Low' && 'This user demonstrates normal transfer behavior with good approval rates and reasonable planning times.'}
            {patterns?.risk_level === 'Medium' && 'This user shows some concerning patterns. Review transfer history carefully before approval.'}
            {patterns?.risk_level === 'High' && 'This user exhibits multiple red flags including frequent transfers, low approval rates, or last-minute requests. Requires thorough review.'}
          </p>
        </div>
      </div>
    </div>
  );
};